import { useEffect, useMemo } from 'react';
import type { TakiSdk } from '@taki/sdk';
import type { FicheDePhoto, PhotosDeFichesPort } from '@taki/ui';

/**
 * LES PHOTOS DE REFERENCE DES FICHES, cote hote (`docs/socle/couleur-photo-fiche.md`).
 *
 * `@taki/ui` dessine la pastille et ne connait que le contexte `PhotosDeFiches`
 * (charger, envoyer, retirer). Ce module est le fournisseur : il parle au SDK
 * (`sdk.files`, nature `photo`) et rend des URL d'objet.
 *
 * PARTAGE PAR L'ECRAN GENERIQUE ET PAR UN ECRAN D'APP ECRIT A LA MAIN, comme
 * `contenuDeCarteFichiers` : une app qui monte son propre `ViewHost` appelle
 * `useFournisseurDePhotos(sdk)` et enveloppe son ecran dans
 * `<PhotosDeFiches.Provider value={...}>`.
 *
 * AUCUNE URL DE TELECHARGEMENT DURABLE : on lit les OCTETS (`sdk.files.download`,
 * donc les droits de la fiche s'appliquent a chaque lecture) et on en fait un blob.
 * Le cache est PAR ID, dans le fournisseur : une liste de cent lignes qui montre
 * trois fois la meme photo ne la lit qu'une fois, et une pastille qui se remonte
 * en defilant la retrouve sans clignoter. Les URL d'objet sont REVOQUEES au
 * demontage du fournisseur (`liberer`).
 */

/** Ce que le fournisseur lit du SDK : seulement les fichiers. */
export type SdkDePhotos = Pick<TakiSdk, 'files'>;

export interface FournisseurDePhotos extends PhotosDeFichesPort {
  /** Revoque toutes les URL d'objet et vide le cache. */
  liberer(): void;
}

/**
 * Le type MIME d'une image, lu sur ses premiers octets : le fichier est range
 * sans son type cote lecture (`download` rend des octets), et un blob sans type
 * se fait renifler par le navigateur, qui se trompe sur un WebP en bien des
 * endroits. Repli : JPEG, la reduction de `@taki/ui` n'en produit pas d'autre.
 */
export function typeDImage(octets: Uint8Array): string {
  const est = (...attendu: number[]): boolean => attendu.every((octet, rang) => octets[rang] === octet);
  if (est(0x89, 0x50, 0x4e, 0x47)) return 'image/png';
  if (est(0x47, 0x49, 0x46, 0x38)) return 'image/gif';
  if (est(0x52, 0x49, 0x46, 0x46) && octets[8] === 0x57 && octets[9] === 0x45 && octets[10] === 0x42 && octets[11] === 0x50) {
    return 'image/webp';
  }
  return 'image/jpeg';
}

export function creerFournisseurDePhotos(sdk: SdkDePhotos): FournisseurDePhotos {
  /* Une URL quand la photo est lue, `null` quand elle ne se lit pas. */
  const lues = new Map<string, string | null>();
  /* Les lectures en cours, pour que deux pastilles du meme id n'en lancent qu'une. */
  const enCours = new Map<string, Promise<string | null>>();

  const url = (id: string): string | undefined => lues.get(id) ?? undefined;

  return {
    enCache: url,

    charger(fileId) {
      if (lues.has(fileId)) return Promise.resolve(lues.get(fileId) ?? null);
      const deja = enCours.get(fileId);
      if (deja !== undefined) return deja;
      const lecture = sdk.files.download(fileId).then(
        (octets) => {
          const lue = URL.createObjectURL(new Blob([octets as BlobPart], { type: typeDImage(octets) }));
          lues.set(fileId, lue);
          enCours.delete(fileId);
          return lue;
        },
        (cause: unknown) => {
          /* Une photo qui ne se lit pas (supprimee, droits) ne fait pas tomber la
             fiche : les initiales tiennent la place. Pas de cache de l'echec, la
             lecture suivante retente. */
          console.warn(`[taki/app-runtime] Photo ${fileId} illisible :`, cause);
          enCours.delete(fileId);
          return null;
        },
      );
      enCours.set(fileId, lecture);
      return lecture;
    },

    async envoyer(fiche: FicheDePhoto, fichier) {
      const stocke = await sdk.files.attach({
        parent: { collection: fiche.collection, id: fiche.id },
        name: fichier.name,
        mimeType: fichier.type,
        bytes: fichier,
        kind: 'photo',
      });
      /* Les octets qu'on vient d'envoyer SONT la photo : on les garde, la
         pastille n'a pas a les relire. */
      lues.set(stocke.id, URL.createObjectURL(fichier));
      return stocke.id;
    },

    async retirer(fileId) {
      await sdk.files.remove(fileId);
      const lue = lues.get(fileId);
      if (typeof lue === 'string') URL.revokeObjectURL(lue);
      lues.delete(fileId);
    },

    liberer() {
      for (const lue of lues.values()) if (typeof lue === 'string') URL.revokeObjectURL(lue);
      lues.clear();
      enCours.clear();
    },
  };
}

/**
 * LE FOURNISSEUR D'UN ECRAN : un par SDK, libere au demontage. `null` (un ecran
 * sans SDK) : pas de fournisseur, donc pas de photo.
 */
export function useFournisseurDePhotos(sdk: SdkDePhotos | null): PhotosDeFichesPort | null {
  const fournisseur = useMemo(() => (sdk === null ? null : creerFournisseurDePhotos(sdk)), [sdk]);
  useEffect(() => () => fournisseur?.liberer(), [fournisseur]);
  return fournisseur;
}
